/**
 * USGS Earthquake data source.
 * Fetches real-time earthquake data from the USGS GeoJSON feed.
 * Free, no API key required. Public domain.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'usgs';

function magnitudeToSeverity(mag: number): Severity {
  if (mag >= 7.0) return 'critical';
  if (mag >= 5.0) return 'high';
  if (mag >= 4.0) return 'medium';
  if (mag >= 2.5) return 'low';
  return 'info';
}

interface UsgsFeature {
  id: string;
  properties: {
    mag: number | null;
    place: string | null;
    time: number;
    url: string;
    title: string;
    alert: string | null;
    tsunami: number;
    type: string;
  };
  geometry: {
    coordinates: [number, number, number]; // [lng, lat, depth]
  };
}

interface UsgsResponse {
  type: string;
  features: UsgsFeature[];
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const usgsSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'USGS Earthquakes',
    description: 'US Geological Survey earthquake data',
    icon: 'activity',
    category: 'environmental',
    updateInterval: 300_000, // 5 min
    requiresApiKey: false,
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] USGS earthquake source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/usgs`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`USGS API returned ${response.status}`);
      }

      const data: UsgsResponse = await response.json();

      if (!data.features || !Array.isArray(data.features)) {
        return [];
      }

      const events: NormalizedEvent[] = [];
      for (const feature of data.features) {
        const mag = feature.properties.mag ?? 0;
        const place = feature.properties.place ?? 'Unknown location';
        const [lng, lat, depth] = feature.geometry.coordinates;

        if (lat == null || lng == null) continue;

        events.push({
          id: `usgs-${feature.id}`,
          source: SOURCE_ID,
          timestamp: new Date(feature.properties.time).toISOString(),
          type: 'earthquake',
          title: `M${mag.toFixed(1)} — ${place}`,
          severity: magnitudeToSeverity(mag),
          location: {
            lat,
            lng,
            name: place,
          },
          tags: ['earthquake', `magnitude-${Math.floor(mag)}`],
          links: feature.properties.url
            ? [{ url: feature.properties.url, label: 'USGS Detail' }]
            : [],
          raw: {
            magnitude: mag,
            depth,
            tsunami: feature.properties.tsunami,
            alert: feature.properties.alert,
            type: feature.properties.type,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
